import {
  IPrSimulatedLayerImperial,
  IPrSimulatedLayerMetric,
  IWesleaConfig,
  IWesleaResult
} from "@/modules/perRoad/interfaces/monteCarlo";
import {
  IPavementStructureScenarioImperial,
  IPavementStructureScenarioMetric
} from "@/modules/structure/interfaces/pavementStructure";
import {getPeakHorizontalStrain} from "@/modules/perRoad/lib/calculations/horizontalStrainDistribution";
import {PerRoadLayerType} from "@/modules/perRoad/interfaces/perRoad";

export const getResultItems = (
  wesleaResults: IWesleaResult[],
  wesleaConfigs: IWesleaConfig[],
  simulatedLayers: IPrSimulatedLayerImperial[] | IPrSimulatedLayerMetric[],
  scenario: IPavementStructureScenarioImperial
) => {

  const items: any[] = [];

  /**
   * Get results for all the AC layers, since they're the only ones where fatigue results are meaningful
   */
  const acLayerNumbers = scenario.structureLayers.filter(layer => [PerRoadLayerType.AC, PerRoadLayerType.CRACKED_AC].includes(layer.type)).map((layer, i) => i + 1);
  const acResults = wesleaResults.filter(r => acLayerNumbers.includes(r.layer));
  const peakAcHorizStrain = getPeakHorizontalStrain(acResults);
  const peakAcVerticalStrain = Math.max(...acResults.map(r => r.normal_strain.z));
  const keyAcResults = acResults.filter(r => {
    return (r.normal_strain.x === peakAcHorizStrain || r.normal_strain.y === peakAcHorizStrain);
  });

  keyAcResults.forEach(r => {
    items.push({
      layer: r.layer,
      //@ts-ignore: doesn't like the union type
      type: simulatedLayers.filter(l => l.layerNumber === r.layer)[0].type,
      x: r.location.x,
      y: r.location.y,
      z: r.location.z,
      // xStrain: r.normal_strain.x,
      // yStrain: r.normal_strain.y,
      horizStrain: peakAcHorizStrain,
      verticalStrain: peakAcVerticalStrain,
      deflection: null,
      keyFatigueResult: true,
      underfatigueLimit: Math.abs(peakAcHorizStrain) < Math.abs(scenario.fatigueEnduranceLimit),
      fatigueLoads: (scenario.fatigueParamA * (10 ** (-6))) * ((1 / Math.abs(peakAcHorizStrain)) ** (scenario.fatigueParamB)),
      keyRuttingResult: false,
      ruttingLoads: null,
    });
  });

  /**
   * Get deflection results
   */

  const subgradeLayerNumbers: number[] = [];
  scenario.structureLayers.forEach((layer, i) => {
    if (![PerRoadLayerType.AC, PerRoadLayerType.CRACKED_AC].includes(layer.type)) {
      subgradeLayerNumbers.push(i + 1);
    }
  });
  const subgradeResults = wesleaResults.filter(r => subgradeLayerNumbers.includes(r.layer));
  const peakVerticalStrain = Math.max(...subgradeResults.map(r => r.normal_strain.z));
  const keyVerticalStrainResults = subgradeResults.filter(r => r.normal_strain.z === peakVerticalStrain);


  keyVerticalStrainResults.forEach(r => {
    items.push({
      layer: r.layer,
      //@ts-ignore: doesn't like the union type
      type: simulatedLayers.filter(l => l.layerNumber === r.layer)[0].type,
      x: r.location.x,
      y: r.location.y,
      z: r.location.z,
      horizStrain: null,
      deflection: null,
      verticalStrain: peakVerticalStrain,
      keyFatigueResult: false,
      underfatigueLimit: false,
      fatigueLoads: null,
      keyRuttingResult: true,
      ruttingLoads: (scenario.fatigueParamA * (10 ** (-6))) * ((1 / Math.abs(r.normal_strain.z)) ** (scenario.ruttingParamB)),
    });
  });

  return items;
};

export const getLayerResultItems = (
  wesleaResults: IWesleaResult[],
  simulatedLayer: IPrSimulatedLayerImperial | IPrSimulatedLayerMetric,
  scenario: IPavementStructureScenarioImperial | IPavementStructureScenarioMetric
) => {

  const items: any[] = [];
  /**
   * Get results for all the AC layers, since they're the only ones where fatigue results are meaningful
   */
  const peakAcHorizStrain = getPeakHorizontalStrain(wesleaResults);
  const peakVerticalStrain = Math.max(...wesleaResults.map(r => r.normal_strain.z));
  const keyResults = wesleaResults.filter(r => {
    return (r.normal_strain.x === peakAcHorizStrain || r.normal_strain.y === peakAcHorizStrain);
  });
  keyResults.push(...wesleaResults.filter(r => r.normal_strain.z === peakVerticalStrain));

  keyResults.forEach(r => {
    items.push({
      layer: r.layer,
      type: null,
      x: r.location.x,
      y: r.location.y,
      z: r.location.z,
      horizStrain: peakAcHorizStrain,
      verticalStrain: peakVerticalStrain,
      deflection: null,
      keyFatigueResult: r.normal_strain.x === peakAcHorizStrain || r.normal_strain.y === peakAcHorizStrain,
      underfatigueLimit: Math.abs(peakAcHorizStrain) < Math.abs(scenario.fatigueEnduranceLimit),
      fatigueLoads: (scenario.fatigueParamA * (10 ** (-6))) * ((1 / Math.abs(peakAcHorizStrain)) ** (scenario.fatigueParamB)),
      keyRuttingResult: r.normal_strain.z === peakVerticalStrain,
      ruttingLoads: (scenario.fatigueParamA * (10 ** (-6))) * ((1 / Math.abs(r.normal_strain.z)) ** (scenario.ruttingParamB)),
    });
  });
  return items;
};
